import { Component, CUSTOM_ELEMENTS_SCHEMA, ViewChild, ElementRef, Input, OnInit, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BriefApplicationResponseDto, BriefResponseDto, SocialChannelEnum } from '../../../../common/general-types/briefs.dto';
import { WalletService } from '@/app/services/wallet/wallet.service';
import { WalletTransactionResponseDto } from '@/app/common/general-types/wallet-transaction.dto';
import { DatePipe } from '@angular/common';
import { CurrencyPipe } from '@angular/common';
import { PayoutsService } from '@/app/services/payouts/payouts.service';
import { FilterCreatorsDto } from '@/app/common/general-types/filters.dto';
import { PayoutDto, PayoutStatusEnum } from '@/app/common/general-types/payouts.dto';
import { SubmittedPostsService } from '@/app/services/submitted-posts/submitted-posts.service';
import { SubmittedPostDto, SubmittedPostStatusEnum } from '@/app/common/general-types/submitted-posts.dto';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { FormatService } from '@/app/common/format/format.service';
import { PricingModelV2Service } from '@/app/services/pricing-model-v2/pricing-model-v2.service';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CreatorService } from '@/app/services/creator/creator.service';
import {
  AirwallexBeneficiaryBankDetailsDto,
  AirwallexBeneficiaryDto,
  AirwallexValidationErrorResponse,
  BANK_COUNTRY_PRESETS,
  BeneficiaryAddressDto,
  BeneficiaryEntityType,
  CreateShoutBeneficiaryDto,
  PayerEntityType,
} from '@/app/pages/admin-options/settings/molecules/creator-payment-details-fix/creator-payment-details.types';
import { AdminCreatorPaymentDetailsService } from '@/app/pages/admin-options/settings/services/admin-creator-payment-details.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';


/** Flat form model for creating a new beneficiary (one source of bank details per region). */
interface CreateBeneficiaryModel {
  entity_type: BeneficiaryEntityType;
  bankCountryPreset: string;
  street_address: string;
  city: string;
  postcode: string;
  state: string;
  account_name: string;
  account_number: string;
  account_routing_value1: string;
  account_routing_value2: string;
  bank_account_category: 'Checking' | 'Savings';
}

@Component({
  selector: 'vs-payment-status',
  imports: [DatePipe, CurrencyPipe],
  templateUrl: './payment-status.html',
  styleUrl: './payment-status.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PaymentStatus implements OnInit {

  @ViewChild('paymentStatusDrawerDialog', { static: false }) paymentStatusDrawerDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('paymentStatusDrawerButton', { static: false }) paymentStatusDrawerButton?: ElementRef<HTMLButtonElement>;

  @Input() brief!: BriefResponseDto;
  @Input() creatorId!: string;
  @Input() focussedApplication: BriefApplicationResponseDto | undefined = undefined;
  @Input() creator: CreatorResponseDto | undefined = undefined;

  PayoutStatusEnum = PayoutStatusEnum;
  SubmittedPostStatusEnum = SubmittedPostStatusEnum;
  SocialChannelEnum = SocialChannelEnum;

  private dialogCloseHandler?: () => void;

  walletTransactions = signal<WalletTransactionResponseDto[]>([]);
  payouts = signal<PayoutDto[]>([]);
  submittedPosts = signal<SubmittedPostDto[]>([]);
  hasPaymentDetails = signal<boolean | undefined>(undefined);
  generalPaymentDetailsRecord = signal<any | undefined>(undefined);

  bankDetails = signal<AirwallexBeneficiaryDto | undefined>(undefined);
  bankDetailsEditing = signal(false);
  bankDetailsCreating = signal(false);
  bankDetailsDraft = signal<AirwallexBeneficiaryDto | undefined>(undefined);

  readonly entityTypeOptions: BeneficiaryEntityType[] = ['PERSONAL', 'COMPANY'];
  readonly payerEntityTypeOptions: PayerEntityType[] = ['PERSONAL', 'COMPANY'];
  readonly bankAccountCategoryOptions = ['Checking', 'Savings'] as const;

  /** Create-beneficiary flow (preset-based; mirrors CreatorPaymentDetailsFix). */
  readonly countryPresets = BANK_COUNTRY_PRESETS;

  createModel = signal<CreateBeneficiaryModel>({
    entity_type: 'PERSONAL' as BeneficiaryEntityType,
    bankCountryPreset: 'GB',
    street_address: '',
    city: '',
    postcode: '',
    state: '',
    account_name: '',
    account_number: '',
    account_routing_value1: '',
    account_routing_value2: '',
    bank_account_category: 'Checking' as 'Checking' | 'Savings',
  });

  createValidatePassed = signal(false);
  createActionLoading = signal(false);

  selectedPreset = computed(() => {
    const code = this.createModel().bankCountryPreset;
    return BANK_COUNTRY_PRESETS.find((p) => p.bankCountryCode === code) ?? BANK_COUNTRY_PRESETS[0];
  });

  isSwiftCountry = computed(() => {
    const code = this.selectedPreset().bankCountryCode;
    return code !== 'GB' && code !== 'US';
  });

  isUsCountry = computed(() => this.selectedPreset().bankCountryCode === 'US');

  showCreateButton = computed(() => this.bankDetailsCreating() && this.createValidatePassed());

  /** Services */
  private walletService = inject(WalletService);
  private payoutsService = inject(PayoutsService);
  private submittedPostsService = inject(SubmittedPostsService);
  public formatService = inject(FormatService);
  public pricingModelService = inject(PricingModelV2Service);
  public alertService = inject(AlertService);
  private currentUserService = inject(CurrentUserService);
  private creatorService = inject(CreatorService);
  private paymentDetailsService = inject(AdminCreatorPaymentDetailsService);
  private toastNotificationService = inject(ToastNotificationService);
  private destroyRef = inject(DestroyRef);
  /** Services - END */

  /** Access roles permitted to use the "Pay anyway" override (GodMode, FinanceAdmin, PayoutAdmin). */
  private readonly payAnywayRoles = [2, 5, 7];
  private readonly bankDetailsAccessKey = '3341b43d-4981-49a8-a5af-31a623b18b4f';

  ngOnInit(): void {
    this.getWalletTransactions();
    this.getPayouts();
    this.getSubmittedPosts();
    this.hasBankDetails();
    // this.getCreatorBankDetails();
  }

  private getBankDetailsCreatorId(): string | undefined {
    const creatorId = this.creator?.id?.trim();
    return creatorId || undefined;
  }

  getWalletTransactions() {
    console.log('getWalletTransactions()');
    this.walletService.getTransactionsByApplication(this.focussedApplication!.id).subscribe((response: any) => {
      console.log('Transactions Response: ', response);
      this.walletTransactions.set(response);
    });
  }

  getPayouts() {
    console.log('getPayouts()');
    this.payoutsService.filterCreatorApplicationSpecific(this.focussedApplication!.id).subscribe((response: any) => {
      console.log('Payouts Response: ', response);
      this.payouts.set(response);
    });
  }

  getSubmittedPosts() {
    console.log('getSubmittedPosts()');
    this.submittedPostsService.getSubmittedPostsForBriefApplication(this.focussedApplication!.id).subscribe((response: any) => {
      console.log('Submitted Posts Response: ', response);
      this.submittedPosts.set(response);
    });
  }

  hasBankDetails() {
    console.log('hasBankDetails()');
    const creatorId = this.getBankDetailsCreatorId();
    if (!creatorId) {
      this.hasPaymentDetails.set(false);
      return;
    }

    this.creatorService.hasBankDetails(creatorId).subscribe({
      next: (response) => {
        console.log('Has Bank Details Response: ', response);
        this.hasPaymentDetails.set(!!response);
      },
      error: (err) => {
        console.error('Error checking bank details: ', err);
        this.hasPaymentDetails.set(false);
      },
    });
  }

  async requestBankDetailsAccess(): Promise<void> {
    const result = await this.alertService.prompt({
      title: 'Access bank details',
      message: 'Enter the access key to view or manage bank details.',
      inputLabel: 'Access key',
      inputPlaceholder: 'Enter key',
      confirmText: 'Continue',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
      showInput: true,
    });

    if (!result.confirmed) {
      return;
    }

    if (result.value.trim() !== this.bankDetailsAccessKey) {
      await this.alertService.alert({
        title: 'Access denied',
        message: 'Incorrect access key.',
        confirmText: 'OK',
        cancelText: '',
        type: 'alert',
        icon: 'error',
      });
      return;
    }

    this.getCreatorBankDetails();
  }

  // getCreatorBankDetails() {
  //   console.log('getCreatorBankDetails()');
  //   const creatorId = this.getBankDetailsCreatorId();
  //   if (!creatorId) {
  //     return;
  //   }

  //   this.paymentDetailsService.getBeneficiaryAdmin(creatorId).subscribe((response: any) => {
  //     console.log('Creator Bank Details Response: ', response);
  //     this.generalPaymentDetailsRecord.set(response);
  //     if (response.airwallex) {
  //       this.bankDetailsCreating.set(false);
  //       this.bankDetailsEditing.set(false);
  //       this.bankDetailsDraft.set(undefined);
  //       this.bankDetails.set(response.airwallex);
  //     } else {
  //       this.startBankDetailsCreateMode();
  //     }
  //   }, err => {
  //     console.error('Error getting creator bank details: ', err);
  //   });
  // }
  getCreatorBankDetails() {
    console.log('getCreatorBankDetails()');
    this.creatorService.getBeneficiaryAdmin(this.creatorId).subscribe((response: any) => {
      console.log('Creator Bank Details Response: ', response);
      this.generalPaymentDetailsRecord.set(response);
      if (response.airwallex) {
        this.bankDetailsCreating.set(false);
        this.bankDetailsEditing.set(false);
        this.bankDetailsDraft.set(undefined);
        this.bankDetails.set(response.airwallex);
      } else {
        this.startBankDetailsCreateMode();
      }
    }, err => {
      console.error('Error getting creator bank details: ', err);
    });
  }

  closeDrawer() {
    this.paymentStatusDrawerDialog?.nativeElement?.close();
  }

  toggleBankDetailsEdit(): void {
    if (this.bankDetailsCreating()) {
      return;
    }

    if (this.bankDetailsEditing()) {
      this.bankDetailsEditing.set(false);
      this.bankDetailsDraft.set(undefined);
      return;
    }

    const current = this.bankDetails();
    if (!current) {
      return;
    }

    this.bankDetailsDraft.set(structuredClone(current));
    this.bankDetailsEditing.set(true);
  }

  cancelBankDetailsEdit(): void {
    if (this.bankDetailsCreating()) {
      this.bankDetailsDraft.set(this.createEmptyBankDetailsDraft());
      return;
    }

    this.bankDetailsEditing.set(false);
    this.bankDetailsDraft.set(undefined);
  }

  startBankDetailsCreateMode(): void {
    this.bankDetails.set(undefined);
    this.bankDetailsCreating.set(true);
    this.bankDetailsEditing.set(true);
    this.bankDetailsDraft.set(this.createEmptyBankDetailsDraft());
    this.createValidatePassed.set(false);
    this.createActionLoading.set(false);
    this.createModel.set({
      entity_type: 'PERSONAL',
      bankCountryPreset: 'GB',
      street_address: '',
      city: '',
      postcode: '',
      state: '',
      account_name: '',
      account_number: '',
      account_routing_value1: '',
      account_routing_value2: '',
      bank_account_category: 'Checking',
    });
  }

  /** Update a single field on the flat create model; any edit forces re-validation. */
  updateCreateField<K extends keyof CreateBeneficiaryModel>(
    field: K,
    value: CreateBeneficiaryModel[K],
  ): void {
    this.createModel.update((m) => ({ ...m, [field]: value }));
    this.createValidatePassed.set(false);
  }

  onCreateCountryPresetChange(event: Event): void {
    this.updateCreateField('bankCountryPreset', (event.target as HTMLSelectElement).value);
  }

  onCreateEntityTypeChange(event: Event): void {
    this.updateCreateField('entity_type', (event.target as HTMLSelectElement).value as BeneficiaryEntityType);
  }

  onCreateBankAccountCategoryChange(event: Event): void {
    this.updateCreateField('bank_account_category', (event.target as HTMLSelectElement).value as 'Checking' | 'Savings');
  }

  onCreateTextFieldInput(
    field: 'street_address' | 'city' | 'postcode' | 'state' | 'account_name' | 'account_number' | 'account_routing_value1' | 'account_routing_value2',
    event: Event,
  ): void {
    this.updateCreateField(field, (event.target as HTMLInputElement).value);
  }

  async runValidate(): Promise<void> {
    const creatorId = this.getBankDetailsCreatorId();
    if (!creatorId) {
      return;
    }

    const body = this.buildCreatePayload();
    if (!body) {
      return;
    }

    this.createActionLoading.set(true);

    this.paymentDetailsService
      .validateBeneficiaryAdmin(creatorId, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.createActionLoading.set(false);
          const err = this.extractValidationError(response);
          if (err) {
            this.createValidatePassed.set(false);
            void this.toastNotificationService.alert({
              title: 'Validation failed',
              message: err,
              type: 'error',
              icon: 'error',
            });
            return;
          }

          this.createValidatePassed.set(true);
          void this.toastNotificationService.alert({
            title: 'Validation passed',
            message: 'Airwallex accepted these bank details. You can create the beneficiary.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: AirwallexValidationErrorResponse }) => {
          this.createActionLoading.set(false);
          this.createValidatePassed.set(false);
          void this.toastNotificationService.alert({
            title: 'Validation failed',
            message: this.formatHttpError(err?.error) ?? 'Request failed.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  async runCreate(): Promise<void> {
    if (!this.createValidatePassed()) {
      void this.toastNotificationService.alert({
        title: 'Validate first',
        message: 'Run validation successfully before creating the Airwallex beneficiary.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    const creatorId = this.getBankDetailsCreatorId();
    if (!creatorId) {
      return;
    }

    const body = this.buildCreatePayload();
    if (!body) {
      return;
    }

    const confirmed = await this.alertService.confirm({
      title: 'Create Airwallex beneficiary',
      message: `Create beneficiary for this creator? This sets airwallexId on their payment detail row.`,
      confirmText: 'Create',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) {
      return;
    }

    this.createActionLoading.set(true);

    this.paymentDetailsService
      .createBeneficiaryAdmin(creatorId, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.createActionLoading.set(false);
          this.createValidatePassed.set(false);
          void this.toastNotificationService.alert({
            title: 'Beneficiary created',
            message: 'Bank details were created and linked to the creator.',
            type: 'success',
            icon: 'success',
          });
          this.hasPaymentDetails.set(true);
          // Reload so the drawer drops back into read-only display mode.
          this.getCreatorBankDetails();
        },
        error: (err: { error?: AirwallexValidationErrorResponse }) => {
          this.createActionLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Create failed',
            message: this.formatHttpError(err?.error) ?? 'Request failed.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private buildCreatePayload(): CreateShoutBeneficiaryDto | null {
    const m = this.createModel();
    const preset = BANK_COUNTRY_PRESETS.find((p) => p.bankCountryCode === m.bankCountryPreset);
    if (!preset) {
      return null;
    }

    if (!m.account_name.trim() || !m.account_number.trim()) {
      void this.toastNotificationService.alert({
        title: 'Missing bank fields',
        message: 'Account name and account number (or IBAN) are required.',
        type: 'error',
        icon: 'error',
      });
      return null;
    }

    const bank_details: CreateShoutBeneficiaryDto['bank_details'] = {
      account_name: m.account_name.trim(),
      account_number: m.account_number.trim().replace(/\s/g, ''),
      account_routing_value1: m.account_routing_value1.trim(),
      bank_country_code: preset.bankCountryCode,
      account_currency: preset.currency,
    };

    if (this.isSwiftCountry() && m.account_routing_value2.trim()) {
      bank_details.account_routing_value2 = m.account_routing_value2.trim();
    }

    if (this.isUsCountry()) {
      bank_details.bank_account_category = m.bank_account_category;
    }

    const payload: CreateShoutBeneficiaryDto = {
      entity_type: m.entity_type,
      bank_details,
    };

    if (this.isUsCountry() || this.isSwiftCountry()) {
      if (!m.city.trim() || !m.street_address.trim() || !m.postcode.trim()) {
        void this.toastNotificationService.alert({
          title: 'Address required',
          message: 'Street, city, and postcode are required for this country.',
          type: 'error',
          icon: 'error',
        });
        return null;
      }

      payload.address = {
        street_address: m.street_address.trim(),
        city: m.city.trim(),
        postcode: m.postcode.trim(),
        state: m.state.trim() || m.city.trim(),
        country_code: preset.bankCountryCode,
      };
    }

    return payload;
  }

  private extractValidationError(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const body = response as AirwallexValidationErrorResponse;
    if (body.code === 'validation_failed') {
      return this.formatHttpError(body);
    }
    return null;
  }

  private formatHttpError(body: AirwallexValidationErrorResponse | undefined): string | null {
    if (!body) {
      return null;
    }
    const first = body.details?.errors?.[0];
    if (first?.message) {
      return first.message;
    }
    return body.message ?? null;
  }

  createEmptyBankDetailsDraft(): AirwallexBeneficiaryDto {
    return {
      id: '',
      payer_entity_type: 'COMPANY',
      transfer_methods: ['LOCAL'],
      beneficiary: {
        entity_type: 'PERSONAL',
        type: 'BANK_ACCOUNT',
        address: {
          street_address: '',
          city: '',
          postcode: '',
          state: '',
          country_code: '',
        },
        bank_details: {
          account_name: '',
          account_currency: '',
          bank_country_code: '',
          bank_name: '',
          iban: '',
          account_number: '',
          account_routing_type1: '',
          account_routing_value1: '',
          account_routing_value2: '',
        },
      },
    };
  }

  formatTransferMethods(methods: string[] | undefined): string {
    return methods?.length ? methods.join(', ') : '—';
  }

  transferMethodsInputValue(methods: string[] | undefined): string {
    return methods?.join(', ') ?? '';
  }

  displayValue(value: string | undefined | null): string {
    return value?.trim() ? value : '—';
  }

  obfuscateSensitiveValue(value: string | undefined | null, visibleSuffix = 4): string {
    const trimmed = value?.trim();
    if (!trimmed) {
      return '—';
    }
    if (trimmed.length <= visibleSuffix) {
      return '*'.repeat(trimmed.length);
    }
    return `${'*'.repeat(Math.max(4, trimmed.length - visibleSuffix))}${trimmed.slice(-visibleSuffix)}`;
  }

  updateDraftTopLevelField(field: 'payer_entity_type', value: PayerEntityType): void {
    this.bankDetailsDraft.update((draft) => draft ? { ...draft, [field]: value } : draft);
  }

  updateDraftTransferMethods(value: string): void {
    const methods = value
      .split(',')
      .map((method) => method.trim())
      .filter(Boolean);

    this.bankDetailsDraft.update((draft) => draft ? { ...draft, transfer_methods: methods } : draft);
  }

  updateDraftEntityType(value: BeneficiaryEntityType): void {
    this.bankDetailsDraft.update((draft) => {
      if (!draft) {
        return draft;
      }

      return {
        ...draft,
        beneficiary: {
          ...draft.beneficiary,
          entity_type: value,
        },
      };
    });
  }

  updateDraftAddressField(field: keyof BeneficiaryAddressDto, value: string): void {
    this.bankDetailsDraft.update((draft) => {
      if (!draft) {
        return draft;
      }

      return {
        ...draft,
        beneficiary: {
          ...draft.beneficiary,
          address: {
            ...(draft.beneficiary.address ?? {
              street_address: '',
              city: '',
              postcode: '',
              state: '',
              country_code: '',
            }),
            [field]: value,
          },
        },
      };
    });
  }

  updateDraftBankField(
    field: keyof AirwallexBeneficiaryBankDetailsDto,
    value: string,
  ): void {
    this.bankDetailsDraft.update((draft) => {
      if (!draft) {
        return draft;
      }

      const bankDetails = {
        ...draft.beneficiary.bank_details,
        [field]: value || undefined,
      };

      return {
        ...draft,
        beneficiary: {
          ...draft.beneficiary,
          bank_details: bankDetails,
        },
      };
    });
  }

  onDraftPayerEntityTypeChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as PayerEntityType;
    this.updateDraftTopLevelField('payer_entity_type', value);
  }

  onDraftTransferMethodsInput(event: Event): void {
    this.updateDraftTransferMethods((event.target as HTMLInputElement).value);
  }

  onDraftEntityTypeChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as BeneficiaryEntityType;
    this.updateDraftEntityType(value);
  }

  onDraftAddressFieldInput(field: keyof BeneficiaryAddressDto, event: Event): void {
    this.updateDraftAddressField(field, (event.target as HTMLInputElement).value);
  }

  onDraftBankFieldInput(field: keyof AirwallexBeneficiaryBankDetailsDto, event: Event): void {
    this.updateDraftBankField(field, (event.target as HTMLInputElement).value);
  }

  onDraftBankAccountCategoryChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.bankDetailsDraft.update((draft) => {
      if (!draft) {
        return draft;
      }

      return {
        ...draft,
        beneficiary: {
          ...draft.beneficiary,
          bank_details: {
            ...draft.beneficiary.bank_details,
            bank_account_category: value
              ? value as AirwallexBeneficiaryBankDetailsDto['bank_account_category']
              : undefined,
          },
        },
      };
    });
  }

  getStatementStatusLabel(transaction: WalletTransactionResponseDto): string {
    return transaction?.reservedForPurchase ? 'Reserved' : 'Released';
  }

  getStatusColour(payoutStatus: PayoutStatusEnum) {
    switch (payoutStatus) {
      case PayoutStatusEnum.Created:
        return 'bg-orange-50 text-orange-700 ring-orange-600 ring-opacity-20 dark:bg-orange-900/30 dark:text-orange-200 dark:ring-orange-700/40';
      case PayoutStatusEnum.Succeeded:
        return 'bg-green-50 text-green-700 ring-green-600 ring-opacity-20 dark:bg-green-900/30 dark:text-green-200 dark:ring-green-700/40';
      case PayoutStatusEnum.ProviderPreparing:
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600 ring-opacity-20 dark:bg-yellow-900/30 dark:text-yellow-200 dark:ring-yellow-700/40';
      case PayoutStatusEnum.Failed:
        return 'bg-red-50 text-red-700 ring-red-600 ring-opacity-20 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-700/40';
      case PayoutStatusEnum.AwaitingCmApproval:
        return 'bg-cyan-50 text-cyan-700 ring-cyan-600 ring-opacity-20 dark:bg-cyan-900/30 dark:text-cyan-200 dark:ring-cyan-700/40';
      default:
        return 'bg-primary/10 text-accent ring-primary/25 dark:bg-primary/25 dark:text-foreground dark:ring-primary/35';
    }
  }

  getPayoutAttemptStatus(payoutStatus: PayoutStatusEnum) {
    switch (payoutStatus) {
      case PayoutStatusEnum.Created:
        return 'Created';
      case PayoutStatusEnum.Succeeded:
        return 'Succeeded';
      case PayoutStatusEnum.ProviderPreparing:
        return 'Preparing';
      case PayoutStatusEnum.Failed:
        return 'Failed';
      case PayoutStatusEnum.AwaitingCmApproval:
        return 'CM App.';
      case PayoutStatusEnum.AwaitingFunds:
        return 'Awaiting Funds';
      case PayoutStatusEnum.AwaitingUserConfirmation:
        return 'Await App.';
      case PayoutStatusEnum.InsufficientFunds:
        return 'Insufficient Funds';
      case PayoutStatusEnum.Sent:
        return 'Sent to provider';
      default:
        return 'All';
    }
  }

  getSocialChannelName(socialChannel: SocialChannelEnum) {
    switch (socialChannel) {
      case SocialChannelEnum['TikTok Post']:
        return 'TikTok';
      case SocialChannelEnum['Instagram Reel']:
        return 'Instagram Reel';
      case SocialChannelEnum['YouTube Shorts']:
        return 'YouTube Shorts';
      case SocialChannelEnum['Instagram Story']:
        return 'Instagram Story';
      case SocialChannelEnum['Snapchat Spotlight']:
        return 'Snapchat Spotlight';
      case SocialChannelEnum['Snapchat Story']:
        return 'Snapchat Story';
      default:
        return 'UGC Only';
    }
    return 'Unknown';
  }

  getSubmittedPostStatusColour(submittedPostStatus: SubmittedPostStatusEnum) {
    switch (submittedPostStatus) {
      case SubmittedPostStatusEnum.Valid:
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600 ring-opacity-20 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-700/40';
      case SubmittedPostStatusEnum.Processing:
        return 'bg-sky-50 text-sky-700 ring-sky-600 ring-opacity-20 dark:bg-sky-900/30 dark:text-sky-200 dark:ring-sky-700/40';
      default:
        return 'bg-primary/10 text-accent ring-primary/25 dark:bg-primary/25 dark:text-foreground dark:ring-primary/35';
    }
  }

  getSubmittedPostStatus(submittedPostStatus: SubmittedPostStatusEnum) {
    switch (submittedPostStatus) {
      case SubmittedPostStatusEnum.Valid:
        return 'Valid';
      case SubmittedPostStatusEnum.Processing:
        return 'Processing';
      default:
        return 'Unknown';
    }
  }

  getPostValue(submittedPost: SubmittedPostDto) {}

  getAudiencePrice(channel: SocialChannelEnum) {
    if (!this.creator || !this.brief) {
      return 0;
    }

    return this.pricingModelService.getAudiencePrice(channel, this.brief, this.creator);
  }

  getCreatorEarnings(socialChannel: SocialChannelEnum) {
    let totalCost = this.getAudiencePrice(socialChannel) * this.brief?.videosPerCreator;
    let commission = totalCost * (this.brief?.currentPrice?.commissionPercentage ?? 0) / 100;
    return totalCost - commission;
  }

  getPlatformFollowers(socialChannel: SocialChannelEnum) {
    switch (socialChannel) {
      case SocialChannelEnum['TikTok Post']:
        return this.creator?.tikTokFollowers;
      case SocialChannelEnum['Instagram Reel']:
        return this.creator?.instaFollowers;
      case SocialChannelEnum['Instagram Story']:
        return this.creator?.instaFollowers;
      case SocialChannelEnum['Snapchat Spotlight']:
        return this.creator?.snapchatFollowers;
      case SocialChannelEnum['Snapchat Story']:
        return this.creator?.snapchatFollowers;
      case SocialChannelEnum['YouTube Shorts']:
        return this.creator?.youtubeSubscribers;
      default:
        return 0;
    }
  }

  getBasePrice() {
    if (this.brief && this.brief.currentPrice) {
      const basePrice = this.pricingModelService.calculateBaseSpendCreateBrief(this.brief.currentPrice)
      return basePrice / 100;
    } else {
      return 0;
    }
  }

  getBaseEarnings() {
    if (this.brief && this.brief.currentPrice) {
      let totalCost = this.getBasePrice() * this.brief?.videosPerCreator;
      let commission = totalCost * (this.brief?.currentPrice?.commissionPercentage ?? 0) / 100;
      return totalCost - commission;
    } else {
      return 0;
    }
  }

  async confirmPayout(submittedPostId: string) {
    console.log('confirmPayout()', submittedPostId);

    const confirmed = await this.alertService.confirm({
      title: 'Confirm Payout',
      message: 'Are you sure you want to confirm this payout?',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'info'
    });

    if (confirmed) {
      // The backend runs the full cycle server-side: create wallet transaction,
      // create payout, and send the amount to Airwallex for processing.
      this.runPayout(submittedPostId, this.submittedPostsService.payForSubmittedPost(submittedPostId), true);
    }
  }

  /**
   * Subscribe to a payout request and surface the result. On an insufficient-balance
   * failure (only when `allowOverride` is set), admins with a payout role are offered
   * the "Pay anyway" override instead of a plain error.
   */
  private runPayout(submittedPostId: string, payment$: Observable<unknown>, allowOverride: boolean) {
    payment$.subscribe({
      next: () => {
        this.alertService.alert({
          title: 'Payout Started',
          message: 'The payout has been created and sent for processing.',
          confirmText: 'OK',
          cancelText: '',
          type: 'alert',
          icon: 'success'
        });
        // Refresh so the new payout and wallet transaction appear.
        this.getPayouts();
        this.getWalletTransactions();
        this.getSubmittedPosts();
      },
      error: (error: HttpErrorResponse) => {
        const message = error?.error?.message ?? 'Something went wrong while processing the payout.';
        const isInsufficientBalance = error?.status === 400 && message.toLowerCase().includes('insufficient');

        if (allowOverride && isInsufficientBalance && this.currentUserService.hasAnyRole(this.payAnywayRoles)) {
          this.offerPayAnyway(submittedPostId);
          return;
        }

        this.alertService.alert({
          title: 'Payout Failed',
          message,
          confirmText: 'OK',
          cancelText: '',
          type: 'alert',
          icon: 'error'
        });
      }
    });
  }

  /** Admin-only fallback: confirm and force the payment through, letting the wallet go negative. */
  private async offerPayAnyway(submittedPostId: string) {
    const payAnyway = await this.alertService.confirm({
      title: 'Insufficient Wallet Balance',
      message: 'This payment exceeds the available wallet balance. Pay anyway and let the wallet go into a negative balance?',
      confirmText: 'Pay anyway',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning'
    });

    if (payAnyway) {
      this.runPayout(submittedPostId, this.submittedPostsService.forcePayForSubmittedPost(submittedPostId), false);
    }
  }
}
